import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import HairstylistCard from './HairstylistCard';
import { logSearchView } from '../utils/hairstylistAnalytics';
import styles from './HairstylistList.module.css';

function HairstylistList({
  hairstylists,
  loading,
  error,
  hasMore,
  loadMore,
  serviceLookup,
  serviceCategoryLookup,
  activeFiltersSummary = [],
}) {
  const sentinelRef = useRef(null);
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [localOrder, setLocalOrder] = useState(hairstylists);
  const loggedViewsRef = useRef(new Set()); // Pour éviter les doublons dans la même session

  // Vérifier si l'utilisateur est admin
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const isAdminUser = currentUser && displayName?.toLowerCase() === 'admin';

  // Mettre à jour l'ordre local quand hairstylists change
  useEffect(() => {
    setLocalOrder(hairstylists);
    // Réinitialiser les vues enregistrées quand la liste change (nouvelle recherche)
    loggedViewsRef.current.clear();
  }, [hairstylists]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { rootMargin: '400px 0px 0px 0px' },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadMore, loading]);

  const handleDragStart = (e, index) => {
    if (!isAdminUser) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    if (!isAdminUser) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    if (!isAdminUser) return;
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    if (!isAdminUser || draggedIndex === null) return;
    e.preventDefault();

    const newOrder = [...localOrder];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);

    setLocalOrder(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
    // Note: L'ordre est seulement local, pas sauvegardé dans Firestore
  };

  const handleDragEnd = () => {
    if (!isAdminUser) return;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const displayOrder = isAdminUser ? localOrder : hairstylists;

  // Pendant le chargement initial (liste vide), afficher un état de chargement dédié au lieu des anciens résultats
  if (loading && hairstylists.length === 0) {
    return (
      <div className={styles.listWrapper}>
        <div className={styles.listLoading} role="status" aria-live="polite">
          <div className={styles.listLoadingSpinner} aria-hidden="true" />
          <p className={styles.listLoadingText}>{t('search.searching')}</p>
        </div>
      </div>
    );
  }

  // Composant wrapper pour enregistrer les vues
  const TrackedCard = ({ stylist, index, ...props }) => {
    const cardRef = useRef(null);

    useEffect(() => {
      const cardElement = cardRef.current;
      if (!cardElement || loggedViewsRef.current.has(stylist.id)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            // Enregistrer l'événement search_view
            logSearchView(stylist.id, currentUser?.uid || null, activeFiltersSummary || []);
            loggedViewsRef.current.add(stylist.id);
            observer.disconnect();
          }
        },
        { threshold: 0.1 } // Déclencher quand 10% de la carte est visible
      );

      observer.observe(cardElement);

      return () => observer.disconnect();
    }, [stylist.id, currentUser, activeFiltersSummary]);

    return (
      <div ref={cardRef}>
        <HairstylistCard
          stylist={stylist}
          {...props}
        />
      </div>
    );
  };

  return (
    <div className={styles.listWrapper}>
      {displayOrder.map((stylist, index) => (
        <TrackedCard
          key={stylist.id}
          stylist={stylist}
          index={index}
          serviceLookup={serviceLookup}
          serviceCategoryLookup={serviceCategoryLookup}
          isDraggable={isAdminUser}
          dragIndex={index}
          isDragged={draggedIndex === index}
          isDragOver={dragOverIndex === index}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      ))}

      {loading && (
        <div className={styles.status}>{t('common.loading')}</div>
      )}

      {error && (
        <div className={styles.error}>
          Une erreur est survenue, merci de réessayer.
        </div>
      )}

      {!loading && !hairstylists.length && (
        <div className={styles.status}>{t('search.noResults')}</div>
      )}

      <div ref={sentinelRef} className={styles.sentinel} />

      {!hasMore && hairstylists.length > 0 && (
        <div className={styles.status}>{t('search.allResultsShown')}</div>
      )}
    </div>
  );
}

export default HairstylistList;

